// src/components/features/analytics/AnalyticsFilters.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  Popover,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  Tune as FilterIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  SaveAlt as SaveIcon,
  RestartAlt as ResetIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';

import DateRangePicker from '../../ui/DateRangePicker';

/**
 * AnalyticsFilters component
 * A comprehensive filters panel for analytics features
 */
const AnalyticsFilters = ({
  filters,
  onFiltersChange,
  onDateRangeChange,
  dateRange,
  filterOptions,
  showDateRange = true,
  initialExpanded = false, 
  allowSaveFilters = false,
  presetFilterSets = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down('sm') || '(max-width: 600px)');
  
  // States
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(filters || {});
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  
  // Calculate active filter count
  const activeFilterCount = Object.values(activeFilters).filter(value => {
    // Check for non-empty values (arrays, strings, or selected values)
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value !== 'all' && value !== '';
    return value !== null && value !== undefined;
  }).length;
  
  // Toggle drawer
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  // Handle filter menu open
  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  
  // Handle filter menu close
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };
  
  // Handle date picker open
  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };
  
  // Handle date picker close
  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };
  
  // Handle filter change
  const handleFilterChange = (filterKey, value) => {
    const newFilters = { ...activeFilters, [filterKey]: value };
    setActiveFilters(newFilters);
  };
  
  // Apply filters
  const applyFilters = () => {
    onFiltersChange(activeFilters);
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      handleFilterMenuClose();
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    // Reset to default values
    const defaultFilters = {};
    filterOptions.forEach(filter => {
      if (filter.type === 'select') {
        defaultFilters[filter.key] = filter.defaultValue || 'all';
      } else if (filter.type === 'multi-select') {
        defaultFilters[filter.key] = filter.defaultValue || [];
      } else if (filter.type === 'checkbox') {
        defaultFilters[filter.key] = filter.defaultValue || false;
      }
    });
    
    setActiveFilters(defaultFilters);
    onFiltersChange(defaultFilters);
    
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      handleFilterMenuClose();
    }
  };
  
  // Handle date range change
  const handleDateChange = (startDate, endDate) => {
    onDateRangeChange(startDate, endDate);
    handleDatePickerClose();
  };
  
  // Save current filter set
  const saveCurrentFilters = () => {
    // This would typically save to user preferences or backend
    console.log('Saving filter set:', activeFilters);
    // Close drawer/menu
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      handleFilterMenuClose();
    }
  };
  
  // Load a preset filter set
  const loadPresetFilters = (presetId) => {
    const preset = presetFilterSets.find(p => p.id === presetId);
    if (preset) {
      setActiveFilters(preset.filters);
      onFiltersChange(preset.filters);
    }
    // Close drawer/menu
    if (isMobile) {
      setDrawerOpen(false);
    } else {
      handleFilterMenuClose();
    }
  };
  
  // Render filter based on its type
  const renderFilter = (filter) => {
    const { key, label, type, options, helperText } = filter;
    
    switch (type) {
      case 'select':
        return (
          <FormControl
            key={key}
            fullWidth
            size="small"
            margin="dense"
          >
            <InputLabel id={`${key}-label`}>{label}</InputLabel>
            <Select
              labelId={`${key}-label`}
              id={key}
              value={activeFilters[key] || 'all'}
              label={label}
              onChange={(e) => handleFilterChange(key, e.target.value)}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {helperText}
              </Typography>
            )}
          </FormControl>
        );
        
      case 'multi-select':
        return (
          <FormControl
            key={key}
            fullWidth
            size="small"
            margin="dense"
          >
            <InputLabel id={`${key}-label`}>{label}</InputLabel>
            <Select
              labelId={`${key}-label`}
              id={key}
              multiple
              value={activeFilters[key] || []}
              label={label}
              onChange={(e) => handleFilterChange(key, e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = options.find(opt => opt.value === value);
                    return (
                      <Chip 
                        key={value} 
                        label={option ? option.label : value} 
                        size="small"
                      />
                    );
                  })}
                </Box>
              )}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={(activeFilters[key] || []).indexOf(option.value) > -1} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {helperText}
              </Typography>
            )}
          </FormControl>
        );
        
      case 'checkbox':
        return (
          <FormGroup key={key}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!activeFilters[key]}
                  onChange={(e) => handleFilterChange(key, e.target.checked)}
                  name={key}
                />
              }
              label={
                <Typography variant="body2">{label}</Typography>
              }
            />
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5, ml: 4 }}>
                {helperText}
              </Typography>
            )}
          </FormGroup>
        );
        
      default:
        return null;
    }
  };
  
  // Filter panel content (shared between drawer and popover)
  const filterPanelContent = (
    <Box sx={{ width: isMobile ? 'auto' : 320, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={500}>
          Filters
        </Typography>
        {isMobile && (
          <IconButton onClick={toggleDrawer} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      
      {presetFilterSets.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom>
            Saved Filter Sets
          </Typography>
          <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {presetFilterSets.map((preset) => (
              <Chip
                key={preset.id}
                label={preset.name}
                onClick={() => loadPresetFilters(preset.id)}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}
      
      <Stack spacing={2}>
        {filterOptions.map(renderFilter)}
      </Stack>
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button 
          size="small" 
          onClick={resetFilters}
          startIcon={<ResetIcon />}
          variant="outlined"
          color="inherit"
        >
          Reset
        </Button>
        
        {allowSaveFilters && (
          <Button 
            size="small" 
            onClick={saveCurrentFilters}
            startIcon={<SaveIcon />}
            variant="outlined"
          >
            Save
          </Button>
        )}
        
        <Button 
          size="small" 
          onClick={applyFilters}
          startIcon={<CheckIcon />}
          variant="contained"
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
  
  return (
    <Box>
      {/* Desktop view: Filters button with popover and separate date picker button */}
      {!isMobile && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {showDateRange && (
            <>
              <Button 
                variant="outlined" 
                size="small"
                startIcon={<DateRangeIcon />}
                onClick={handleDatePickerOpen}
                sx={{ textTransform: 'none' }}
              >
                {dateRange?.startDate.toLocaleDateString()} - {dateRange?.endDate.toLocaleDateString()}
              </Button>
              
              <Popover
                open={Boolean(datePickerAnchor)}
                anchorEl={datePickerAnchor}
                onClose={handleDatePickerClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <Box sx={{ p: 2, width: 320 }}>
                  <DateRangePicker
                    startDate={dateRange?.startDate}
                    endDate={dateRange?.endDate}
                    onChange={handleDateChange}
                    fullWidth
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button 
                      size="small" 
                      onClick={handleDatePickerClose}
                      variant="outlined"
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
              </Popover>
            </>
          )}
          
          <Badge badgeContent={activeFilterCount} color="primary">
            <Button 
              variant="outlined" 
              size="small"
              startIcon={<FilterIcon />}
              onClick={handleFilterMenuOpen}
              endIcon={<ExpandMoreIcon />}
            >
              Filters
            </Button>
          </Badge>
          
          <Popover
            open={Boolean(filterMenuAnchor)}
            anchorEl={filterMenuAnchor}
            onClose={handleFilterMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {filterPanelContent}
          </Popover>
        </Box>
      )}
      
      {/* Mobile view: Combined filters button with drawer */}
      {isMobile && (
        <Box>
          <Tooltip title="Filters and Date Range">
            <Badge badgeContent={activeFilterCount} color="primary">
              <IconButton
                onClick={toggleDrawer}
                size="small"
                color="primary"
                sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
              >
                <FilterIcon />
              </IconButton>
            </Badge>
          </Tooltip>
          
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer}
            PaperProps={{
              sx: { width: '85%', maxWidth: 360 }
            }}
          >
            <Box sx={{ p: 2 }}>
              {filterPanelContent}
              
              {showDateRange && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                    Date Range
                  </Typography>
                  <DateRangePicker
                    startDate={dateRange?.startDate}
                    endDate={dateRange?.endDate}
                    onChange={handleDateChange}
                    fullWidth
                  />
                </>
              )}
            </Box>
          </Drawer>
        </Box>
      )}
    </Box>
  );
};

AnalyticsFilters.propTypes = {
  /** Current filter values */
  filters: PropTypes.object,
  /** Callback when filters change */
  onFiltersChange: PropTypes.func.isRequired,
  /** Current date range */
  dateRange: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
  }),
  /** Callback when date range changes */
  onDateRangeChange: PropTypes.func.isRequired,
  /** Filter definitions */
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'multi-select', 'checkbox']).isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          label: PropTypes.string.isRequired,
        })
      ),
      helperText: PropTypes.string,
      defaultValue: PropTypes.any,
    })
  ).isRequired,
  /** Show date range picker */
  showDateRange: PropTypes.bool,
  /** Initially expanded state */
  initialExpanded: PropTypes.bool,
  /** Allow saving filter sets */
  allowSaveFilters: PropTypes.bool,
  /** Preset filter sets */
  presetFilterSets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      filters: PropTypes.object.isRequired,
    })
  ),
};

export default AnalyticsFilters;